import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkGitHubAlerts } from "remark-github-markdown-alerts";
import Alert from "@imminently/decisively-ui/Alert";
import { Children, isValidElement, type ReactNode } from "react";

import { parsePageTrail } from "@/features/adapters/pageLinks";
import { PageLinkBreadcrumb } from "@/ui/blocks/PageLinkBreadcrumb";

export interface AdapterMarkdownProps {
  content: string;
  compact?: boolean;
}

const alertVariant = {
  note: "info",
  tip: "success",
  important: "info",
  warning: "warning",
  caution: "destructive",
} as const;

/** The plain text of rendered Markdown children, such as a link's text. */
const textOf = (children: ReactNode): string =>
  Children.toArray(children)
    .map((child) => (typeof child === "string" || typeof child === "number"
      ? String(child)
      : isValidElement<{ children?: ReactNode }>(child) ? textOf(child.props.children) : ""))
    .join("");

const hasClassName = (className: string | undefined, value: string): boolean =>
  Boolean(className?.split(" ").includes(value));

export const AdapterMarkdown = ({ content, compact = false }: AdapterMarkdownProps) => {
  const bodyClassName = compact ? "text-xs leading-5" : "text-sm leading-6";

  return (
    <div className={compact ? "grid min-w-0 max-w-full gap-1.5 overflow-hidden" : "grid min-w-0 max-w-full gap-2 overflow-hidden"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkGitHubAlerts, { mode: "component" }]]}
        components={{
          h2: ({ children }) => <h2 className="text-lg font-semibold text-foreground">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-semibold text-foreground">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-semibold text-foreground">{children}</h4>,
          p: ({ children }) => <p className={`${bodyClassName} break-words text-foreground`}>{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          code: ({ children }) => <code className="break-all">{children}</code>,
          ul: ({ children, className }) => (
            <ul
              className={`grid min-w-0 gap-1 ${bodyClassName} text-foreground ${hasClassName(className, "contains-task-list") ? "list-none pl-0" : "list-disc pl-5"}`}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`grid min-w-0 list-decimal gap-1 pl-5 ${bodyClassName} text-foreground`}>{children}</ol>
          ),
          li: ({ children }) => <li className="min-w-0 break-words">{children}</li>,
          // A plain quote is a callout; `> [!WARNING]` and the other GitHub alert types pick the variant.
          blockquote: ({ children }) => (
            <Alert variant="info">
              <Alert.Content><Alert.Description>{children}</Alert.Description></Alert.Content>
            </Alert>
          ),
          div: ({ children, className, node, ...props }) => {
            if (hasClassName(className, "markdown-alert")) {
              const type = node?.properties?.["data-alert-type"] ?? node?.properties?.dataAlertType;
              const variant = typeof type === "string" && type in alertVariant
                ? alertVariant[type as keyof typeof alertVariant]
                : "info";
              return (
                <Alert variant={variant}>
                  <Alert.Content>{children}</Alert.Content>
                </Alert>
              );
            }
            if (hasClassName(className, "markdown-alert-title")) return <Alert.Title>{children}</Alert.Title>;
            if (hasClassName(className, "markdown-alert-content")) {
              return <Alert.Description>{children}</Alert.Description>;
            }
            return (
              <div
                className={className}
                {...props}
              >
                {children}
              </div>
            );
          },
          span: ({ children, className, ...props }) =>
            hasClassName(className, "markdown-alert-icon") ? null : (
              <span
                className={className}
                {...props}
              >
                {children}
              </span>
            ),
          table: ({ children }) => (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className={`w-full border-collapse text-left ${bodyClassName}`}>{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border-b border-border bg-muted px-3 py-2 font-semibold text-foreground">{children}</th>,
          td: ({ children }) => <td className="border-b border-border px-3 py-2 text-foreground">{children}</td>,
          del: ({ children }) => <del>{children}</del>,
          input: ({ type, ...props }) => (
            <input
              className="mr-2 align-middle accent-primary"
              type={type}
              {...props}
            />
          ),
          a: ({ children, href }) => {
            // An internal link written as a navigation path (`[Build → File processing](/build/…)`) is a page link.
            const trail = href?.startsWith("/") ? parsePageTrail(textOf(children)) : undefined;
            if (href && trail) {
              return <PageLinkBreadcrumb trail={trail} to={href} />;
            }
            return (
              <a
                className="font-medium text-primary hover:underline"
                href={href}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export const AdapterContent = ({ content }: { content: string }) => <AdapterMarkdown content={content} />;
